import Header from "@/components/header";
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Group,
  Image,
  LoadingOverlay,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useState } from "react";

import CustomStepper from "@/components/stepper";
import useCheckNationalIDImage from "@/services/verification/hooks/useCheckNationalIDImage";
import { IconHandClick, IconIdBadge2, IconTrash } from "@tabler/icons-react";
import ImageUploading, {
  ImageListType,
  ImageType,
} from "react-images-uploading";
import { steppersData } from "../type";
import { useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";
import { useGetVerification } from "@/services/verification/hooks/useGetVerification";
import useIsSmall from "@/hooks/useIsSmall";

function VerificationNationalID() {
  const [images, setImages] = React.useState<ImageType[]>([]);
  const [uploaderLoading, setUploaderLoading] = useState<boolean>();
  const [editedImage, setEditedImage] = useState<Blob | null>(null);
  const isSmall = useIsSmall();
  const navigate = useNavigate();
  const { mutate, isLoading } = useCheckNationalIDImage();
  const { data: verification, isLoading: verificationLoading } =
    useGetVerification({ isAdmin: true });

  useEffect(() => {
    if (verification?.nationalCardImage)
      setEditedImage(verification?.nationalCardImage);
  }, [verification]);

  const submit = () => {
    if (images[0].file)
      mutate(images[0].file, {
        onSuccess() {
          navigate("/dashboard/profile/verification/result");
        },
      });
  };

  const resizeImage = async (file: File) => {
    const options = {
      maxSizeMB: 0.5, // Compress to a maximum size
      maxWidthOrHeight: 1000, // Resize maintaining aspect ratio
      useWebWorker: true,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error("Error resizing image:", error);
      throw error;
    }
  };

  const onChange = async (imageList: ImageListType) => {
    setUploaderLoading(true);
    const [imageFile] = imageList;
    if (imageFile?.file) {
      const compressedImage = await resizeImage(imageFile?.file);
      setImages([
        { ...imageFile, file: new File([compressedImage], "untitled") },
      ]);
    } else {
      setImages([]);
    }
    setUploaderLoading(false);
  };
  return (
    <>
      <Header title="تایید هویت" showBack route="/dashboard/profile" />
      <Container w="100%" size="full">
        <Title order={4}> تایید هویت </Title>
      </Container>
      <Container size="sm" w="100%">
        <Stack mih={isSmall ? "calc(100svh - 120px)" : ""} mt="xl">
          <CustomStepper steppers={steppersData} active={1} />
          <Stack flex={1}>
            <Stack flex={1} px="md">
              <Alert>
                <Stack ta="center">
                  <Text size="xs">
                    لطفا عکس کارت ملی متعلق به خود را در باکس زیر آپلود نمایید.
                  </Text>
                </Stack>
              </Alert>
              <Box>
                <ImageUploading
                  multiple
                  value={images}
                  onChange={onChange}
                  maxNumber={1}
                  dataURLKey="data_url"
                >
                  {({ imageList, onImageUpload, onImageRemove, dragProps }) => (
                    // write your building UI
                    <Stack className="upload__image-wrapper">
                      <LoadingOverlay
                        visible={uploaderLoading || verificationLoading}
                        loaderProps={{ type: "dots" }}
                      />
                      {imageList.length === 0 ? (
                        editedImage ? (
                          <Stack>
                            <Card
                              h="200"
                              onClick={onImageUpload}
                              {...dragProps}
                              styles={{
                                root: {
                                  border: "2px dashed green",
                                },
                              }}
                            >
                              <Image
                                h={160}
                                radius="md"
                                fit="cover"
                                src={`data:image/jpeg;base64,${verification?.nationalCardImage}`}
                              />
                            </Card>
                            <Button
                              color="red"
                              size="xs"
                              w="fit-content"
                              variant="light"
                              onClick={() => setEditedImage(null)}
                            >
                              <Group gap="xs">
                                <IconTrash stroke={1} size={20} />
                                <Text size="xs">حذف تصویر</Text>
                              </Group>
                            </Button>
                          </Stack>
                        ) : (
                          <Card
                            h="200"
                            onClick={onImageUpload}
                            {...dragProps}
                            styles={{
                              root: {
                                border: "2px dashed green",
                              },
                            }}
                          >
                            <Stack justify="center" align="center">
                              <IconIdBadge2
                                size={100}
                                stroke={1}
                                color="var(--mantine-color-gray-5)"
                              />
                              <Text ta="center" c="gray.5" size="xs" px="xl">
                                با کلیک{" "}
                                <Text span>
                                  <IconHandClick stroke={1} size={20} />
                                </Text>{" "}
                                بر روی باکس مقابل تصویر کارت ملی خود را آپلود
                                نمایید.
                              </Text>
                            </Stack>
                          </Card>
                        )
                      ) : (
                        imageList.map((image, index) => (
                          <Stack>
                            <Card
                              h="200"
                              onClick={onImageUpload}
                              {...dragProps}
                              styles={{
                                root: {
                                  border: "2px dashed green",
                                },
                              }}
                            >
                              <Image
                                src={image["data_url"]}
                                h={160}
                                radius="md"
                                fit="cover"
                              />
                            </Card>
                            <Button
                              color="red"
                              size="xs"
                              w="fit-content"
                              variant="light"
                              onClick={() => onImageRemove(index)}
                            >
                              <Group gap="xs">
                                <IconTrash stroke={1} size={20} />
                                <Text size="xs">حذف تصویر</Text>
                              </Group>
                            </Button>
                          </Stack>

                          // <div key={index} className="image-item">
                          //   <img src={image["data_url"]} alt="" width="100" />
                          //   <div className="image-item__btn-wrapper">
                          //     <button onClick={() => onImageUpdate(index)}>
                          //       Update
                          //     </button>
                          //     <button onClick={() => onImageRemove(index)}>
                          //       Remove
                          //     </button>
                          //   </div>
                          // </div>
                        ))
                      )}
                    </Stack>
                  )}
                </ImageUploading>
              </Box>
            </Stack>
            <Stack px="md" mb="lg">
              <Button
                type="submit"
                form="information"
                size="md"
                fullWidth
                onClick={submit}
                disabled={images.length === 0}
                loading={isLoading}
              >
                ارسال تصویر
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </>
  );
}

export default VerificationNationalID;
